import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { A4_HEIGHT, A4_WIDTH } from '@/lib/constants/pdf.ts';
import { MAX_PINCH_ZOOM_DELTA_PER_FRAME, MAX_ZOOM_SCALE, MIN_ZOOM_SCALE, ZOOM_STEP } from '@/lib/constants/zoom.ts';
import { usePointerZoomEvents, useWheelZoomEvents } from '@/lib/hooks/use-zoom-events.ts';
import type {
  FixedZoomAnchor,
  GesturePinchSession,
  PanState,
  Point,
  TouchPinchSession,
  UseDocumentElementZoomResult,
  WheelPinchSession,
  ZoomAnchor
} from '@/lib/types/zoom.ts';
import { applyZoomAnchor, clamp, createTrackingZoomAnchor, createZoomAnchor, getNextFitScale, limitZoomScaleDelta, roundZoomScale } from '@/lib/utils/zoom.ts';

type QueuedPinchZoom = {
  fixedZoomAnchor: FixedZoomAnchor;
  point: Point;
  nextZoomScale: number;
};

export const useDocumentElementZoom = ({ isAllowZoom }: { isAllowZoom: boolean }): UseDocumentElementZoomResult => {
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const activePointersRef = useRef<Map<number, Point>>(new Map());
  const pinchStartDistanceRef = useRef<number | null>(null);
  const pinchStartZoomScaleRef = useRef(1);
  const pinchAnchorRef = useRef<FixedZoomAnchor | null>(null);
  const pendingZoomAnchorRef = useRef<ZoomAnchor | null>(null);
  const panStateRef = useRef<PanState | null>(null);
  const queuedPinchZoomRef = useRef<QueuedPinchZoom | null>(null);
  const pinchAnimationFrameRef = useRef<number | null>(null);
  const lastAppliedPinchZoomScaleRef = useRef(1);
  const zoomScaleRef = useRef(1);
  const previewScaleRef = useRef(1);
  const wheelPinchSessionRef = useRef<WheelPinchSession | null>(null);
  const wheelPinchTimeoutRef = useRef<number | null>(null);
  const gesturePinchSessionRef = useRef<GesturePinchSession | null>(null);
  const touchPinchSessionRef = useRef<TouchPinchSession | null>(null);

  const [fitScale, setFitScale] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);
  const [pointerCount, setPointerCount] = useState(0);
  const previewScale = fitScale * zoomScale;

  zoomScaleRef.current = zoomScale;
  previewScaleRef.current = previewScale;
  lastAppliedPinchZoomScaleRef.current = zoomScale;

  const applyNormalizedZoomWithAnchor = useCallback((normalizedZoomScale: number, anchor: ZoomAnchor | null) => {
    const currentZoomScale = zoomScaleRef.current;
    const currentPreviewScale = previewScaleRef.current;

    if (normalizedZoomScale === currentZoomScale) {
      const previewContainer = previewContainerRef.current;

      if (previewContainer && anchor) {
        applyZoomAnchor(previewContainer, anchor, currentPreviewScale);
      }

      return;
    }

    pendingZoomAnchorRef.current = anchor;
    setZoomScale(normalizedZoomScale);
  }, []);

  const setContinuousZoomWithAnchor = useCallback(
    (nextZoomScale: number, anchor: ZoomAnchor | null) => {
      applyNormalizedZoomWithAnchor(clamp(nextZoomScale, MIN_ZOOM_SCALE, MAX_ZOOM_SCALE), anchor);
    },
    [applyNormalizedZoomWithAnchor]
  );

  const setSteppedZoomWithAnchor = useCallback(
    (nextZoomScale: number, anchor: ZoomAnchor | null) => {
      applyNormalizedZoomWithAnchor(roundZoomScale(clamp(nextZoomScale, MIN_ZOOM_SCALE, MAX_ZOOM_SCALE)), anchor);
    },
    [applyNormalizedZoomWithAnchor]
  );

  const flushQueuedPinchZoom = useCallback(() => {
    pinchAnimationFrameRef.current = null;

    const queuedPinchZoom = queuedPinchZoomRef.current;

    if (!queuedPinchZoom) {
      return;
    }

    const normalizedTargetZoomScale = clamp(queuedPinchZoom.nextZoomScale, MIN_ZOOM_SCALE, MAX_ZOOM_SCALE);
    const limitedZoomScale = limitZoomScaleDelta(lastAppliedPinchZoomScaleRef.current, normalizedTargetZoomScale, MAX_PINCH_ZOOM_DELTA_PER_FRAME);
    const previewContainer = previewContainerRef.current;

    lastAppliedPinchZoomScaleRef.current = limitedZoomScale;

    if (!previewContainer) {
      setContinuousZoomWithAnchor(limitedZoomScale, null);
    } else {
      setContinuousZoomWithAnchor(limitedZoomScale, createTrackingZoomAnchor(previewContainer, queuedPinchZoom.fixedZoomAnchor, queuedPinchZoom.point));
    }

    if (limitedZoomScale === normalizedTargetZoomScale) {
      queuedPinchZoomRef.current = null;
      return;
    }

    pinchAnimationFrameRef.current = window.requestAnimationFrame(flushQueuedPinchZoom);
  }, [setContinuousZoomWithAnchor]);

  const scheduleQueuedPinchZoom = useCallback(() => {
    if (pinchAnimationFrameRef.current !== null) {
      return;
    }

    pinchAnimationFrameRef.current = window.requestAnimationFrame(flushQueuedPinchZoom);
  }, [flushQueuedPinchZoom]);

  const startPan = useCallback((pointerId: number, point: Point) => {
    const previewContainer = previewContainerRef.current;

    if (!previewContainer) {
      return;
    }

    panStateRef.current = {
      pointerId,
      startPointer: point,
      startScrollLeft: previewContainer.scrollLeft,
      startScrollTop: previewContainer.scrollTop,
      startPageScrollTop: ((document.scrollingElement || document.documentElement) as HTMLElement).scrollTop
    };
  }, []);

  const applyPinchZoom = useCallback(
    (fixedZoomAnchor: FixedZoomAnchor, point: Point, nextZoomScale: number) => {
      queuedPinchZoomRef.current = {
        fixedZoomAnchor,
        point,
        nextZoomScale
      };
      scheduleQueuedPinchZoom();
    },
    [scheduleQueuedPinchZoom]
  );

  const setCenteredZoom = useCallback(
    (nextZoomScale: number) => {
      const previewContainer = previewContainerRef.current;

      if (!previewContainer) {
        setSteppedZoomWithAnchor(nextZoomScale, null);
        return;
      }

      setSteppedZoomWithAnchor(
        nextZoomScale,
        createZoomAnchor(
          previewContainer,
          {
            x: previewContainer.clientWidth / 2,
            y: previewContainer.clientHeight / 2
          },
          previewScaleRef.current
        )
      );
    },
    [setSteppedZoomWithAnchor]
  );

  const handleZoomOut = () => {
    setCenteredZoom(zoomScaleRef.current - ZOOM_STEP);
  };

  const handleZoomIn = () => {
    setCenteredZoom(zoomScaleRef.current + ZOOM_STEP);
  };

  const handleResetZoom = () => {
    setCenteredZoom(1);
  };

  const interactionHandlers = usePointerZoomEvents({
    isAllowZoom,
    previewContainerRef,
    activePointersRef,
    pinchStartDistanceRef,
    pinchStartZoomScaleRef,
    pinchAnchorRef,
    panStateRef,
    zoomScaleRef,
    previewScaleRef,
    startPan,
    setContinuousZoomWithAnchor,
    applyPinchZoom,
    onPinchStateChange: setPointerCount
  });

  useWheelZoomEvents({
    isAllowZoom,
    previewContainerRef,
    zoomScaleRef,
    previewScaleRef,
    wheelPinchSessionRef,
    wheelPinchTimeoutRef,
    gesturePinchSessionRef,
    touchPinchSessionRef,
    applyPinchZoom
  });

  useLayoutEffect(() => {
    const previewContainer = previewContainerRef.current;
    const pendingZoomAnchor = pendingZoomAnchorRef.current;

    if (!previewContainer || !pendingZoomAnchor) {
      return;
    }

    applyZoomAnchor(previewContainer, pendingZoomAnchor, previewScale);
    pendingZoomAnchorRef.current = null;
  }, [previewScale]);

  useEffect(() => {
    return () => {
      if (pinchAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(pinchAnimationFrameRef.current);
      }

      queuedPinchZoomRef.current = null;
    };
  }, []);

  useEffect(() => {
    const previewContainer = previewContainerRef.current;

    if (!previewContainer) {
      return;
    }

    const updateFitScale = () => {
      setFitScale(getNextFitScale(previewContainer.clientWidth, previewContainer.clientHeight));
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (!entry) {
        updateFitScale();
        return;
      }

      setFitScale(getNextFitScale(entry.contentRect.width, entry.contentRect.height));
    });

    updateFitScale();
    resizeObserver.observe(isAllowZoom ? document.body : previewContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isAllowZoom]);

  const zoomedWidth = Math.round(A4_WIDTH * previewScale);
  const zoomedHeight = Math.round(A4_HEIGHT * previewScale);
  const isMinZoom = zoomScale <= MIN_ZOOM_SCALE;
  const isMaxZoom = zoomScale >= MAX_ZOOM_SCALE;

  return {
    previewContainerRef,
    previewScale,
    zoomScale,
    zoomedWidth,
    zoomedHeight,
    isMinZoom,
    isMaxZoom,
    pointerCount,
    interactionHandlers,
    handleZoomOut,
    handleZoomIn,
    handleResetZoom
  };
};
